<script>
  import { tick, createEventDispatcher } from 'svelte';
  import Url from '@utils/url';

  import AutocompleteAjax from '@ajax/autocomplete';
  import jQuery from 'jquery';

  const dispatch = createEventDispatcher();

  export let placeholder = '';
  export let mobile = false;
  export let width = 430;
  export let height = 48;
  export let value = null;
  export let mode = 'standard';
  export let inputStyle = '';
  export let singleResultStyle = '';
  export let focus = true;
  export let autocompleteResults = [];
  export let showSearchIcon = true;

  let nameEl;
  let selectedTerm;
  let ajaxCountIndex = 0;

  let typing = false;
  let typingTimeout;
  let dropdown = mode === 'dropdown';

  let closeResults = function () {
    if (autocompleteResults.length > 0) {
      typing = false;
      ajaxCountIndex += 1;
      autocompleteResults = [];
      clearTimeout(typingTimeout);

      if (nameEl) {
        nameEl.blur();
      }
    }
  };

  let handleBlur = function () {
    if (dropdown) {
      setTimeout(() => {
        closeResults();
      }, 250);
    } else {
      searchAutocomplete();
    }

    dispatch('blur');
  };

  let handleKeyDown = function (e) {
    if (dropdown && e.keyCode === 27) {
      e.stopPropagation();
      closeResults();
    } else if (e.keyCode === 38 || e.keyCode === 40) {
      e.preventDefault();
      e.stopPropagation();

      let index = autocompleteResults.indexOf(selectedTerm);

      let newIndex;
      if (e.keyCode === 38) {
        newIndex = index - 1;
        if (newIndex < 0) {
          newIndex = autocompleteResults.length - 1;
        }
      } else {
        newIndex = index + 1;
        if (newIndex > autocompleteResults.length - 1) {
          newIndex = 0;
        }
      }

      selectedTerm = autocompleteResults[newIndex];
    }
  };

  let handleSearchInputEvents = function (e) {
    if (e.keyCode === 13) {
      if (selectedTerm === undefined || autocompleteResults.indexOf(selectedTerm) === 0) {
        selectTerm(nameEl.value);
      } else {
        selectTerm(selectedTerm);
      }

      if (document && document.activeElement) {
        document.activeElement.blur();
      }
    } else if (e.keyCode === 38 || e.keyCode === 40) {
      // no-op
    } else {
      searchAutocomplete();
    }
  };

  let searchAutocomplete = async function () {
    ajaxCountIndex += 1;
    let currentCountIndex = ajaxCountIndex;

    if (nameEl.value.length < 1) {
      autocompleteResults = [];
      return;
    }

    typing = true;
    let resp = await AutocompleteAjax.get(nameEl.value);
    let noResults = autocompleteResults.length === 0;

    if (currentCountIndex === ajaxCountIndex) {
      try {
        autocompleteResults = [value, ...resp.objects, ...resp.results.filter((x) => x != nameEl.value.toLowerCase())];
      } catch {
        autocompleteResults = [];
      }

      selectedTerm = autocompleteResults[0];

      clearTimeout(typingTimeout);

      // we only need to wait 500 milliseconds when there are already previous search results otherwise causes flickering behavior.
      if (noResults) {
        typing = false;
      } else {
        typingTimeout = setTimeout(() => {
          typing = false;
        }, 500);
      }
    }
  };

  let handlePaste = function () {
    setTimeout(() => {
      if (Url.validURL(nameEl.value) && mobile) {
        selectTerm(nameEl.value);
        nameEl.blur();
      } else {
        searchAutocomplete();
      }
    }, 200);
  };

  let selectTerm = function (term) {
    dispatch('selected', { term: term });
    selectedTerm = undefined;

    if (dropdown) {
      closeResults();
    }
  };

  let applyNormalFontToMatchingText = function (term) {
    try {
      return term.replace(new RegExp(nameEl.value, 'gi'), (match) => `<span class='matching-text'>${match}</span>`);
    } catch {
      return term;
    }
  };

  let showTwoLinesForMobile = function (node) {
    if (!mobile) {
      return;
    }

    let jQnode = jQuery(node).removeClass('single-line-text').removeClass('double-line-text');

    let height = jQnode.height();

    if (height > 25) {
      jQnode.addClass('double-line-text');
    }
  };

  let setFocus = async function (node) {
    if (focus) {
      await tick();
      node.focus();
    }
  };
</script>

<style style="text/scss" lang="scss">
  :global(.add-rec-highlight) {
    background-color: #e5e1f4;
    padding-top: 3px;
    padding-bottom: 4px;
    padding: 1px;
    border-radius: 4px;
    font-weight: bold;
  }

  .selected-rec-object {
    display: flex;
    font-size: 17px;
    font-weight: normal;
    border-radius: 15px;
    border: 2px solid transparent;
    padding-left: 21px;
    padding-right: 21px;
    height: 50px;
    padding-top: 15px;
    padding-bottom: 15px;
    line-height: 17px;
    color: #495057;
    width: 100%;
    background: #f5f3fc;
    align-items: center;

    .selected-rec-object-name {
      flex: 1;
      height: 17px;
      margin-right: 15px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .x-button {
      flex-shrink: 0;
      cursor: pointer;
      width: 24px;
      height: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: white;
      border-radius: 999px;
      border: 1px solid transparent;
      transition: 0.1s;

      &:hover {
        border: 1px solid #a893f9;
      }
    }
  }

  .autocomplete-container {
    border: 0px;
    padding: 0px;
    border-top: 0px;
    background: white;
    width: 100%;

    .single-term {
      background: white;
      border-radius: 0px;
      padding: 0px;
      cursor: pointer;
      display: flex;
      min-height: 48px;
      padding-left: 45px;
      padding-right: 20px;
      display: flex;
      align-items: center;
      position: relative;
      font-weight: bold;
    }

    .single-term-without-icon {
      padding-left: 21px;
    }

    .single-term-selected {
      background: #eeebfa;
    }
  }

  .search-input {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    border-radius: 15px;
    border: 2px solid #ececec;
    padding-left: 21px;
    padding-right: 21px;
    height: 50px;
    line-height: 17px;
    color: #495057;
    width: 100%;
    outline: none;
    font-size: 17px;
    font-weight: normal;
    padding-left: 44px;
    word-break: break-all;

    &::placeholder {
      opacity: 0.85;
      font-weight: 400;
    }

    &:focus {
      background-color: #f6f4fe;
      border-color: #a893f9;
      outline: 0;
      outline: none;
    }
  }

  .box-with-results {
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.09);
    border-radius: 12px;
    overflow: hidden;
  }

  .search-input-with-results {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    background-color: white;
    border-color: white;
    margin-bottom: 0px;
    border-radius: 12px;

    &:focus {
      background-color: white;
      border-color: white;
    }
  }

  .add-to-benable {
    margin-left: 8px;
    font-size: 15px;
    line-height: 19px;
    opacity: 0.6;
    flex-shrink: 0;
  }

  .arrow-customization {
    position: absolute;
    left: 18px;
    margin-top: 1px;
    transform: rotate(-45deg);
    color: #a893f9;
  }

  .mode-dropdown {
    border-radius: 0px;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;

    .purple-scrollbar-autocomplete {
      position: absolute;
      z-index: 5;
      width: 100%;
      border: 2px solid #eee;
      border-top: 0px;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
      overflow: hidden;
      box-shadow: 0 3px 15px rgba(0, 0, 0, 0.09);
    }

    .box-with-results {
      width: 100%;
      border-radius: 0px;
    }

    .search-input-with-results {
      border: 2px solid #eee;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    }

    .search-input-without-icon {
      padding-left: 21px;
    }
  }

  .single-line-text {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    -webkit-line-clamp: 1;
  }

  * :global(.double-line-text) {
    margin-top: 10px;
    margin-bottom: 10px;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
  }

  * :global(.matching-text) {
    font-weight: normal;
  }
</style>

<div
  style={mobile ? 'width: 100%' : `width: ${width}px;`}
  class:box-with-results={autocompleteResults.length > 0}
  class:mode-dropdown={dropdown}>
  <div style="position: relative">
    {#if showSearchIcon}
      <i class="fa fa-search" style="position: absolute; left: 17px; top: 17px; opacity: 0.2" />
    {/if}

    <input
      id="select-recommendation-input"
      bind:this={nameEl}
      use:setFocus
      on:paste={handlePaste}
      autocomplete="off"
      class="search-input"
      style={inputStyle}
      class:search-input-with-results={autocompleteResults.length > 0}
      class:search-input-without-icon={!showSearchIcon}
      type="text"
      bind:value={value}
      placeholder={placeholder}
      on:keyup={handleSearchInputEvents}
      on:keydown={handleKeyDown}
      on:blur={() => handleBlur()}
      on:focus={() => {
        searchAutocomplete();
        dispatch('focus');
      }} />
  </div>

  {#if autocompleteResults.length > 0}
    <div class="autocomplete-container" style={mobile ? 'max-width: 100%' : `max-width: ${width}px;`}>
      <div style={`max-height: ${height}px; overflow: auto;`} class="purple-scrollbar-autocomplete">
        {#each autocompleteResults as term, i (term)}
          <div
            on:mouseover={() => {
              if (typing === false) {
                selectedTerm = i === 0 ? nameEl.value : term;
              }
            }}
            on:click={() => selectTerm(i === 0 ? nameEl.value : term)}
            class="single-term"
            class:single-term-without-icon={!showSearchIcon}
            class:single-term-selected={term == selectedTerm}
            style={singleResultStyle}>
            {#if typeof term === 'string'}
              {#each [applyNormalFontToMatchingText(term)] as x (x)}
                <span class="single-line-text" use:showTwoLinesForMobile>{@html x}</span>
              {/each}
            {:else}
              <i class="fa fa-arrow-up arrow-customization" />
              <span
                style=" text-overflow: ellipsis; font-weight: bold; overflow: hidden; white-space: nowrap; -webkit-line-clamp: 1;">
                {term.name1}
              </span>
            {/if}
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>
